import React, { useState, useEffect, useCallback } from 'react';
import { materialsService } from '../services/api';
import { DocumentViewerModal } from './shared/DocumentViewerModal';
import { campo, useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, mensagemDoErroDeCarga } from '../design';
import { useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { estadoDoLink, rotuloDoLink, confirmacaoDeLinkNovo } from '../utils/administrativo';

interface Material {
  id: number;
  code: string;
  title: string;
  description?: string;
  category: string;
  status: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  version: number;
  downloadCount: number;
  createdAt: string;
  publicToken?: string | null;
  publicTokenExpiresAt?: string | null;
}

const CATEGORY_CONFIG: Record<string, { icon: string; color: string; bgColor: string; label: string }> = {
  'Apresentacao': { icon: '📊', color: 'text-blue-400', bgColor: 'bg-blue-500/20 border-blue-500/30', label: 'Apresentação' },
  'Roteiro': { icon: '📝', color: 'text-green-400', bgColor: 'bg-green-500/20 border-green-500/30', label: 'Roteiro' },
  'Argumentario': { icon: '💬', color: 'text-purple-400', bgColor: 'bg-purple-500/20 border-purple-500/30', label: 'Argumentário' },
  'Case de Sucesso': { icon: '🏆', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20 border-yellow-500/30', label: 'Case de Sucesso' },
  'Whitepaper': { icon: '📄', color: 'text-slate-400', bgColor: 'bg-slate-500/20 border-slate-500/30', label: 'Whitepaper' },
  'Video': { icon: '🎬', color: 'text-red-400', bgColor: 'bg-red-500/20 border-red-500/30', label: 'Vídeo' },
  'Imagem': { icon: '🖼️', color: 'text-pink-400', bgColor: 'bg-pink-500/20 border-pink-500/30', label: 'Imagem' },
  'Outro': { icon: '📁', color: 'text-slate-400', bgColor: 'bg-slate-500/20 border-slate-500/30', label: 'Outro' },
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (mimeType: string) => {
  if (mimeType?.startsWith('image/')) return '🖼️';
  if (mimeType?.startsWith('video/')) return '🎬';
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType === 'text/html') return '🌐';
  return '📁';
};

interface MaterialsPortalProps {
  onManageMaterials?: () => void;
}

export default function MaterialsPortal({ onManageMaterials }: MaterialsPortalProps) {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [previewMaterial, setPreviewMaterial] = useState<Material | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allCategories = Object.keys(materialsByCategory);
    setExpandedCategories(new Set(allCategories));
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  const confirmar = useConfirm();
  const avisar = useAviso();
  // D5 (10/10/2026): a carga calava, e a tela dizia "Nenhum material encontrado".
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const loadData = useCallback(async () => {
    await rodarCarga(async () => {
      const data = await materialsService.getAll();
      // Only show active materials
      setMaterials(Array.isArray(data) ? data.filter((m: Material) => m.status === 'Ativo') : []);
    });
  }, [rodarCarga]);
  const temFonte = carga.atualizadoEm !== null;

  useEffect(() => { loadData(); }, [loadData]);

  // Group materials by category
  const materialsByCategory = materials.reduce((acc, material) => {
    const cat = material.category || 'Outro';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(material);
    return acc;
  }, {} as Record<string, Material[]>);

  // Sort categories with "Imagem" at the end
  const sortCategories = (a: string, b: string) => {
    if (a === 'Imagem') return 1;
    if (b === 'Imagem') return -1;
    return a.localeCompare(b);
  };

  // Filter by search
  const filteredCategories = Object.entries(materialsByCategory)
    .map(([category, items]) => ({
      category,
      items: items.filter(m => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
          m.title?.toLowerCase().includes(s) ||
          m.code?.toLowerCase().includes(s) ||
          m.description?.toLowerCase().includes(s)
        );
      })
    }))
    .filter(({ items }) => items.length > 0)
    .filter(({ category }) => !selectedCategory || category === selectedCategory)
    .sort((a, b) => sortCategories(a.category, b.category));

  const handleDownload = async (material: Material) => {
    try {
      await materialsService.download(material.id);
    } catch (e) {
      avisar(`Não consegui baixar o material: ${mensagemDoErroDeCarga(e)}`, 'error');
    }
  };

  const openPreview = async (material: Material) => {
    try {
      const { url } = await materialsService.getDownloadUrl(material.id);
      setPreviewUrl(url);
      setPreviewMaterial(material);
    } catch (e) {
      avisar(`Não consegui abrir o material: ${mensagemDoErroDeCarga(e)}`, 'error');
    }
  };

  // A URL vem do backend com token opaco. Montar `/m/${code}` aqui voltaria a expor o
  // catálogo público à enumeração, que é justamente o que o token resolve.
  const copyLink = async (material: Material) => {
    // D4: no token expirado o servidor gera outro e o link que está com o cliente morre; pergunta antes.
    const link = estadoDoLink(material);
    if (link.tipo === 'expirado' && !await confirmar(confirmacaoDeLinkNovo(material, link.desde))) return;
    try {
      const { url } = await materialsService.createPublicLink(material.id, 30);
      await navigator.clipboard.writeText(url);
      setCopiedCode(material.code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao gerar link público', 'error');
    }
  };

  if (carga.estado === 'carregando' && !temFonte) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Carregando materiais...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <span className="text-3xl">📚</span> Portal de Materiais
          </h1>
          <p className="text-slate-400 mt-1">
            Acesse apresentações, roteiros e materiais de apoio comercial
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm flex items-center gap-1"
            title="Expandir todas"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
            Expandir
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm flex items-center gap-1"
            title="Recolher todas"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
            </svg>
            Recolher
          </button>
          {onManageMaterials && (
            <button
              onClick={onManageMaterials}
              className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-sm"
            >
              Gerenciar Materiais
            </button>
          )}
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-wrap items-center gap-4 bg-central-cartao p-4 rounded-central border border-central-borda">
        <div className="relative flex-1 min-w-[200px]">
          <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Buscar materiais..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 rounded-lg placeholder-slate-500 focus:outline-none ${campo}`}
          />
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              !selectedCategory
                ? 'bg-slate-600 text-white font-semibold'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Todos
          </button>
          {Object.keys(materialsByCategory).sort(sortCategories).map(cat => {
            const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG['Outro'];
            const count = materialsByCategory[cat]?.length || 0;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  selectedCategory === cat
                    ? 'bg-slate-600 text-white font-semibold'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <span>{config.icon}</span>
                {config.label}
                <span className="text-xs opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Materials Grid by Category */}
      <EstadoDaLista carga={carga} temDados={temFonte} oQue="os materiais" onTentarDeNovo={loadData} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
      {filteredCategories.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <div className="text-5xl mb-4">📭</div>
          <p>Nenhum material encontrado</p>
        </div>
      ) : (
        filteredCategories.map(({ category, items }) => {
          const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG['Outro'];
          const isExpanded = expandedCategories.has(category);
          return (
            <div key={category} className="space-y-4">
              {/* Category Header - Clickable */}
              <button
                onClick={() => toggleCategory(category)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border ${config.bgColor} hover:opacity-90 transition-opacity cursor-pointer`}
              >
                <span className="text-2xl">{config.icon}</span>
                <h2 className={`text-lg font-bold ${config.color}`}>{config.label}</h2>
                <span className="text-sm text-slate-400">({items.length} materiais)</span>
                <div className="flex-1" />
                <svg
                  className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Materials Cards - Collapsible */}
              {isExpanded && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(material => (
                  <div
                    key={material.id}
                    className="bg-central-cartao rounded-central border border-central-borda hover:bg-central-elevado transition-colors overflow-hidden"
                  >
                    {/* Card Header */}
                    <div className="p-4 border-b border-slate-700">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{getFileIcon(material.mimeType)}</span>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-medium truncate">{material.title}</h3>
                          <p className="text-slate-500 text-sm">{material.code} | v{material.version}</p>
                        </div>
                      </div>
                      {material.description && (
                        <p className="text-slate-400 text-sm mt-2 line-clamp-2">{material.description}</p>
                      )}
                    </div>

                    {/* Card Footer */}
                    <div className="p-3 bg-slate-900/30">
                      <div className="flex items-center justify-between">
                        <div className="text-slate-500 text-xs">
                          {formatFileSize(Number(material.fileSize))} | {material.downloadCount} downloads
                        </div>
                        <div className="flex items-center gap-1">
                          {(material.mimeType?.startsWith('image/') || material.mimeType === 'application/pdf' || material.mimeType === 'text/html') && (
                            <button
                              onClick={() => openPreview(material)}
                              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                              title="Visualizar"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => handleDownload(material)}
                            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                            title="Download"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </button>
                          <button
                            onClick={() => copyLink(material)}
                            className={`p-2 hover:bg-slate-700 rounded-lg transition-colors ${
                              copiedCode === material.code ? 'text-green-400' : 'text-slate-400 hover:text-white'
                            }`}
                            title={copiedCode === material.code ? 'Link copiado!' : rotuloDoLink(estadoDoLink(material)).botao}
                          >
                            {copiedCode === material.code ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          );
        })
      )}
      </EstadoDaLista>

      {/* Preview Modal (componente compartilhado — ver shared/DocumentViewerModal.tsx) */}
      {previewMaterial && (
        <DocumentViewerModal
          title={previewMaterial.title}
          subtitle={`${previewMaterial.code} · v${previewMaterial.version}`}
          fileUrl={previewUrl}
          mimeType={previewMaterial.mimeType}
          fileName={previewMaterial.fileName}
          loading={!previewUrl}
          accent='primary'
          onClose={() => setPreviewMaterial(null)}
          info={[
            { label: 'Categoria', value: CATEGORY_CONFIG[previewMaterial.category]?.label || previewMaterial.category },
            { label: 'Versão', value: 'v' + previewMaterial.version },
            { label: 'Arquivo', value: previewMaterial.fileName },
            { label: 'Tamanho', value: formatFileSize(Number(previewMaterial.fileSize)) },
            { label: 'Downloads', value: String(previewMaterial.downloadCount ?? 0) },
          ]}
          headerActions={
            <button
              onClick={() => handleDownload(previewMaterial)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200"
              title="Download"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Baixar
            </button>
          }
        />
      )}
    </div>
  );
}
